import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Home, Search, Delete } from '@mui/icons-material';
import { useAPI } from '../../hooks/useAPI';

const MyApartments = ({ apartments = [], onAddApartment, onRemoveApartment, isLoading = false }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [availableApartments, setAvailableApartments] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [adding, setAdding] = useState(false);

  const { searchHousing, addApartmentToProfile } = useAPI();
  const searchHousingRef = useRef(searchHousing);

  // Uppdatera ref när searchHousing ändras
  useEffect(() => {
    searchHousingRef.current = searchHousing;
  }, [searchHousing]);

  // Sök i housing-databasen baserat på sökterm med memoized search function
  const searchApartments = useCallback(async (term) => {
    if (term.trim().length < 2) {
      setAvailableApartments([]);
      return;
    }

    try {
      setSearchLoading(true);
      const results = await searchHousingRef.current(term);
      setAvailableApartments(results || []);
    } catch (error) {
      setAvailableApartments([]);
    } finally {
      setSearchLoading(false);
    }
  }, []); // Tom dependency array - använder ref istället

  useEffect(() => {
    // Debounce sökning
    const timeoutId = setTimeout(() => {
      searchApartments(searchTerm);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, searchApartments]); // Stabil dependency array

  // Filter results to exclude already selected apartments (memoized)
  const filteredApartments = useMemo(() => {
    return availableApartments.filter(apt => {
      const notAlreadySelected = !apartments.some(selected => 
        selected.address === apt.address && selected.apartmentNumber === apt.apartmentNumber
      );
      return notAlreadySelected;
    });
  }, [availableApartments, apartments]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setShowDropdown(true);
  };

  const handleSelectApartment = useCallback(async (apartment) => {
    if (adding) return; // Förhindra dubbla klick

    setAdding(true);
    try {
      // Lägg till lägenheten i databasen - säkerställ att inga undefined värden skickas
      const result = await addApartmentToProfile({
        address: apartment.address || '',
        apartmentNumber: apartment.apartmentNumber || '',
        housingCooperative: apartment.housingCooperative || '',
        city: apartment.city || '',
        zipCode: apartment.zipCode || '',
        // Föreningen måste följa med anspråket. Utan associationId hamnar
        // lägenheten utan koppling, visas som "Okänd förening" och medlemmen
        // kommer inte vidare med sin ansökan.
        ...(apartment.associationId && { associationId: apartment.associationId })
      });

      // Lägg till i lokal state med ID från databasen
      if (result && result.data) {
        onAddApartment({
          id: result.data.id, // Viktigt! Lägg till ID från databasen
          address: result.data.address || apartment.address || '',
          apartmentNumber: result.data.apartmentNumber || apartment.apartmentNumber || '',
          housingCooperative: result.data.housingCooperative || apartment.housingCooperative || '',
          city: result.data.city || apartment.city || '',
          zipCode: result.data.zipCode || apartment.zipCode || '',
          ownershipStatus: result.data.ownershipStatus || null
        });
      }

      setSearchTerm('');
      setShowDropdown(false);
    } catch (error) {
      alert('Kunde inte lägga till lägenhet. Försök igen.');
    } finally {
      setAdding(false);
    }
  }, [adding, addApartmentToProfile, onAddApartment]);

  const handleRemoveApartment = (index) => {
    onRemoveApartment(index);
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <div className="rounded-full bg-[#e6f3fa] p-2">
          <Home className="text-[#62B8E9]" sx={{ fontSize: 20 }} />
        </div>
        <h2 className="text-lg font-semibold text-[#2C2C2C]">Mina lägenheter</h2>
      </div>

      {/* Current Apartments List */}
      {apartments.length === 0 ? (
        <div className="text-center py-6 bg-[#E8E8E8] rounded-lg mb-4">
          <Home className="text-[#6C6C6C] mx-auto mb-2" sx={{ fontSize: 28 }} />
          <p className="text-[#6C6C6C] text-sm">Inga lägenheter valda</p>
        </div>
      ) : (
        <div className="space-y-2 mb-4">
          {apartments.map((apartment, index) => {
            const isPending = apartment.ownershipStatus === 'PENDING_APPROVAL';
            return (
              <div
                key={index}
                className={`flex items-start gap-3 p-3 rounded-lg transition-colors group ${
                  isPending ? 'bg-amber-50 hover:bg-amber-100/70' : 'bg-[#E8E8E8] hover:bg-gray-200'
                }`}
              >
                <div className="rounded-full bg-white p-1.5 mt-0.5">
                  <Home className="text-[#62B8E9]" sx={{ fontSize: 16 }} />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[#2C2C2C] text-sm truncate">
                    {apartment.address}, Lgh {apartment.apartmentNumber}
                  </div>
                  <div className="text-[#6C6C6C] text-xs mt-0.5">
                    {apartment.housingCooperative || 'Okänd förening'}
                  </div>
                  {apartment.city && (
                    <div className="text-[#6C6C6C] text-xs">
                      {apartment.city}
                    </div>
                  )}
                  {isPending && (
                    <div className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 bg-amber-200 text-amber-900 text-[10px] font-semibold rounded-full">
                      ⏳ Väntar på godkännande
                    </div>
                  )}
                </div>

                <button
                  onClick={() => handleRemoveApartment(index)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-[#6C6C6C] hover:text-red-500 hover:bg-white rounded mt-0.5"
                  title="Ta bort lägenhet"
                >
                  <Delete sx={{ fontSize: 16 }} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Search/Add Dropdown */}
      <div className="relative">
        <div className="relative">
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            onFocus={() => setShowDropdown(true)}
            placeholder="Sök och lägg till lägenhet..."
            disabled={adding}
            className="w-full border border-[#E8E8E8] rounded-lg px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-[#62B8E9]/20 focus:border-[#62B8E9] text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <Search className="absolute right-3 top-2.5 text-[#6C6C6C]" sx={{ fontSize: 18 }} />
        </div>

        {/* Dropdown Results */}
        {showDropdown && searchTerm.trim().length >= 2 && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-[#E8E8E8] rounded-lg shadow-lg max-h-48 overflow-y-auto">
            {adding && (
              <div className="p-3 text-center">
                <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-[#62B8E9] mb-2"></div>
                <p className="text-[#6C6C6C] text-sm">Lägger till lägenhet...</p>
              </div>
            )}

            {!adding && searchLoading && (
              <div className="p-3 text-center">
                <p className="text-[#6C6C6C] text-sm">Söker lägenheter...</p>
              </div>
            )}

            {!adding && !searchLoading && filteredApartments.length > 0 && (
              <>
                {filteredApartments.map((apartment) => (
                  <button
                    key={apartment.id || `${apartment.address}-${apartment.apartmentNumber}`}
                    onClick={() => handleSelectApartment(apartment)}
                    disabled={adding}
                    className="w-full text-left px-3 py-3 hover:bg-[#E8E8E8] transition-colors first:rounded-t-lg last:rounded-b-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="font-medium text-[#2C2C2C] text-sm">
                      {apartment.address}, Lgh {apartment.apartmentNumber}
                    </div>
                    <div className="text-[#6C6C6C] text-xs mt-0.5">
                      {apartment.housingCooperative || 'Okänd förening'}
                    </div>
                    <div className="text-[#6C6C6C] text-xs">
                      {apartment.city}
                    </div>
                  </button>
                ))}
              </>
            )}

            {!adding && !searchLoading && filteredApartments.length === 0 && (
              <div className="p-3">
                <p className="text-[#6C6C6C] text-sm text-center">
                  Inga lägenheter hittades för "{searchTerm}"
                </p>
              </div>
            )}
          </div>
        )}

        {/* Show helper text when search is too short */}
        {showDropdown && searchTerm.trim().length > 0 && searchTerm.trim().length < 2 && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-[#E8E8E8] rounded-lg shadow-lg p-3">
            <p className="text-[#6C6C6C] text-sm text-center">
              Skriv minst 2 tecken för att söka...
            </p>
          </div>
        )}
      </div>

      {/* Click outside to close dropdown */}
      {showDropdown && (
        <div 
          className="fixed inset-0 z-5" 
          onClick={() => setShowDropdown(false)}
        />
      )}
    </div>
  );
};

export default MyApartments; 